var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ContactMethod } from "healthex-common/consentconfig.dtos";
import { DocumentRetrievalTaskStatus, DocumentVectorizationStatus } from "healthex-common/document.retrieval.dtos";
import { PatientDataVisualizationPdfStatus } from "healthex-common/patient.data.visualization.dtos";
import { filterNull, willNeverHappen } from "healthex-common/utils";
import { computed, observable } from "mobx";
import { getObservableT } from "../Infra/i18next";
import { NOT_LOADED } from "../Infra/RemoteData";
import { PatientConsentViewModel } from "./PatientConsentViewModel";
import { openAsyncWindow } from "./WindowHelpers";
const t = getObservableT("patients");
export class PatientConsentConfirmedViewModel extends PatientConsentViewModel {
    constructor() {
        super(...arguments);
        this.hasTrackedPageView = false;
        this.aiCountdownSecondsTimer = null;
        this.maxAiCountdownSeconds = 10;
        this.aiCountdownSeconds = this.maxAiCountdownSeconds;
        this.isCompleting = false;
    }
    run() {
        this.disposer.reaction(() => {
            var _a, _b;
            return ({
                isRecordsFound: this.isRecordsFound,
                patientId: (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.patientId,
                studyId: this.study !== NOT_LOADED ? (_b = this.study) === null || _b === void 0 ? void 0 : _b.id : null,
            });
        }, () => {
            var _a;
            if (!this.hasTrackedPageView && ((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.patientId)) {
                this.hasTrackedPageView = true;
                this.trackPageView();
                void this.patientConsentUIManager.deleteConsentFlowProgress();
            }
        }, { name: "trackPageView", fireImmediately: true });
        this.disposer.reaction(() => this.showAiCountdownTimer, (showAiCountdownTimer) => {
            if (showAiCountdownTimer && this.aiCountdownSeconds === this.maxAiCountdownSeconds) {
                this.startAiCountdownTimer();
            }
        }, { name: "showAiCountdownTimer", fireImmediately: true });
    }
    stop() {
        this.disposer.dispose();
        this.clearAiCountdownTimer();
    }
    startAiCountdownTimer() {
        this.clearAiCountdownTimer();
        this.aiCountdownSecondsTimer = window.setInterval(() => {
            if (this.aiCountdownSeconds === 0) {
                this.clearAiCountdownTimer();
                void this.onComplete();
                console.log(`Redirecting to ${this.aiToolName} after waiting for ${this.maxAiCountdownSeconds}.`);
            }
            this.aiCountdownSeconds = Math.max(0, this.aiCountdownSeconds - 1);
        }, 1000);
    }
    clearAiCountdownTimer() {
        if (this.aiCountdownSecondsTimer !== null)
            window.clearInterval(this.aiCountdownSecondsTimer);
        this.aiCountdownSecondsTimer = null;
    }
    trackPageView() {
        var _a, _b, _c;
        heap.track("PatientConsentConfirmed", {
            patientId: (_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.patientId,
            studyId: this.study !== NOT_LOADED ? (_b = this.study) === null || _b === void 0 ? void 0 : _b.id : null,
            batchId: (_c = this.retrieval) === null || _c === void 0 ? void 0 : _c.batchId,
            recordsFound: this.isRecordsFound,
            isAIFlow: this.isAIFlow,
            isVectorizationComplete: this.isVectorizationComplete,
        });
    }
    get zeroLocationsFound() {
        return this.patientConsentUIManager.zeroLocationsFound;
    }
    get isVectorizationComplete() {
        var _a;
        return this.isAIFlow && ((_a = this.retrieval) === null || _a === void 0 ? void 0 : _a.documentVectorizationStatus) === DocumentVectorizationStatus.COMPLETE;
    }
    get isRecordsFound() {
        return this.patientConsentUIManager.mayHaveAccessibleRecordsOnceReady;
    }
    get shouldHideUnifiedRecordButton() {
        if (this.isHealthWalletEnabled)
            return true;
        if (this.authManager.isHostedSsoRestricted)
            return true;
        if (this.isScreenshotMode) {
            return this.recordsSummary === null;
        }
        const retrieval = this.patientConsentUIManager.retrieval;
        if (!retrieval)
            return true;
        if (!retrieval.finalized) {
            return true;
        }
        if (retrieval.finalized && retrieval.pdfStatus !== PatientDataVisualizationPdfStatus.COMPLETED) {
            return true;
        }
        return false;
    }
    get isAddRecordLocationsDirectly() {
        return this.patientConsentUIManager.isAddRecordLocationsDirectly;
    }
    get title() {
        if (this.isAddRecordLocationsDirectly) {
            return t("Success!");
        }
        if (this.isIASAndStillWaitingForFinalization) {
            return this.isHealthWalletEnabled
                ? t("Still Preparing Your HealthEx Wallet")
                : t("We’re still working on your HealthEx Unified Record");
        }
        if (this.isIAS && this.isRecordsFound) {
            return this.isHealthWalletEnabled ? t("Your HealthEx Wallet Is Ready") : t("Your HealthEx Unified Record is ready!");
        }
        if (this.isPreview || this.isScreenshotPreviewMode || this.isRecordsFound) {
            return this.isDataUpdate
                ? this.getCustomizedText("dataUpdateThankYouConsentedTitle")
                : this.getCustomizedText("thankYouConsentedTitle");
        }
        return t("Thank you for your time.");
    }
    get aiToolName() {
        return this.patientConsentUIManager.aiToolName;
    }
    get isIASAndStillWaitingForFinalization() {
        return this.isIAS && this.retrieval && !this.retrieval.finalized;
    }
    get supportingText() {
        var _a;
        if (this.isAddRecordLocationsDirectly) {
            if (this.isIAS) {
                return this.isHealthWalletEnabled
                    ? t("We’re Updating Your HealthEx Wallet With Your Latest Records.")
                    : t("We’re updating your HealthEx Unified Record with your latest records.");
            }
            const orgName = this.isAIFlow ? this.aiToolName : this.organizationName;
            return t("We’re updating your records for {{ orgName }}.", { orgName });
        }
        if (this.isIASAndStillWaitingForFinalization) {
            return t("Things are taking a bit longer than expected. You can leave this page and we’ll let you know when it’s ready.");
        }
        if (this.isIAS) {
            if (this.isRecordsFound) {
                if (this.isHealthWalletEnabled) {
                    return t("Your records from every provider are now in one place.");
                }
                if (this.preferredContactMethod === NOT_LOADED)
                    return "";
                const contactPreference = (_a = this.preferredContactMethod) !== null && _a !== void 0 ? _a : ContactMethod.EMAIL;
                switch (contactPreference) {
                    case ContactMethod.EMAIL:
                        return t("We’ve sent a confirmation to {{ email }}.", { email: this.patientEmail });
                    case ContactMethod.TEXT:
                    case ContactMethod.CALL:
                    case ContactMethod.DO_NOT_CONTACT:
                        return t("Make sure to download your copy.");
                    default:
                        return willNeverHappen(contactPreference);
                }
            }
            else if (this.hasErrorLocations) {
                return t("You've agreed to access your records, however, we couldn’t access them due to a connection issue.");
            }
            else {
                return t("You've agreed to access your records, however, no records are available right now.");
            }
        }
        if (this.isPreview || this.isScreenshotPreviewMode || this.isRecordsFound) {
            return this.isDataUpdate
                ? this.getCustomizedText("dataUpdateThankYouConsentedText")
                : this.getCustomizedText("thankYouConsentedText");
        }
        if (this.isAIFlow) {
            return this.hasErrorLocations
                ? t("You've agreed to connect with {{ aiToolName }}, however, we couldn’t access them due to a connection issue. No records were connected.", {
                    aiToolName: this.aiToolName,
                })
                : t("You agreed to connect your records to {{ aiToolName }}, though no records are actively connected at this time.", {
                    aiToolName: this.aiToolName,
                });
        }
        return t("You've agreed to share your records with {{ orgName }}, however, no records are available right now.", {
            orgName: this.organizationName,
        });
    }
    get confirmationText() {
        if (this.isPreview || this.isScreenshotPreviewMode) {
            return t("We’ll send a confirmation to {{ email }}.", { email: this.patientEmail });
        }
        if (!this.isRecordsFound && !this.isIASAndStillWaitingForFinalization) {
            if (this.isIAS) {
                return t("If your records become available later, you can try again anytime on HealthEx.");
            }
            else if (this.isAIFlow) {
                return t("You can return to add records at any time.");
            }
            else {
                return t("If your records become available later, we may reach out again.");
            }
        }
        if (this.isEmailContactPreference) {
            return t("We’ll send a confirmation to {{ email }}.", { email: this.patientEmail });
        }
        else {
            return t("We’ll send a confirmation to {{ phone }}.", { phone: this.patientPhone });
        }
    }
    get confirmationAccessSettingsText() {
        return this.isHealthWalletEnabled
            ? t("You can change access settings at any time in your HealthEx Wallet.")
            : t("You can review and change access settings at any time on HealthEx.");
    }
    get recordsReadyNotificationText() {
        if (this.preferredContactMethod === NOT_LOADED)
            return "";
        return this.isEmailContactPreference
            ? t("We’ll notify you at {{ email }} when your records are ready.", { email: this.patientEmail })
            : t("We’ll notify you at {{ phone }} when your records are ready.", { phone: this.patientPhone });
    }
    get shareRecordsAnyTimeText() {
        return t("You can share records with providers and services at any time.");
    }
    get addRecordLocationsTimeEstimateText() {
        if (!this.isAIFlow)
            return null;
        return t("Preparing your records usually takes about 10 minutes, but this may take up to an hour.");
    }
    get walletUpdatesAutomaticallyText() {
        return t("You can refresh your records after any visit, and more providers can be added anytime.");
    }
    get openYourWalletButtonText() {
        return t("Open Your Wallet");
    }
    get openHealthExWalletButtonText() {
        return t("Open HealthEx Wallet");
    }
    get goToHealthExButtonText() {
        return t("Go to HealthEx");
    }
    get seeUnifiedRecordButtonText() {
        return t("See my HealthEx Unified Record");
    }
    get addRecordLocationsConfirmButtonText() {
        if (this.isIAS) {
            return this.isHealthWalletEnabledForNavigation ? this.openYourWalletButtonText : this.goToHealthExButtonText;
        }
        if (!this.canRedirectExternally) {
            return this.isHealthWalletEnabledForNavigation ? this.openHealthExWalletButtonText : this.goToHealthExButtonText;
        }
        const label = this.returnToPartnerLabel;
        if (label)
            return label;
        if (this.isAIFlow) {
            return t("Go to {{ aiToolName }}", { aiToolName: this.aiToolName });
        }
        return t("Go back to {{ orgName }}", { orgName: this.hostedOrganizationName });
    }
    get isEmailContactPreference() {
        return this.preferredContactMethod === "EMAIL";
    }
    get organizationLogoUrl() {
        if (this.patientConsentUIManager.studyInfo !== NOT_LOADED && this.patientConsentUIManager.studyInfo !== null) {
            return this.patientConsentUIManager.studyInfo.addedByOrganization.logoUrl;
        }
        return null;
    }
    get errorLocations() {
        if (this.isPreview || this.isScreenshotPreviewMode)
            return null;
        const retrieval = this.patientConsentUIManager.retrieval;
        if (!retrieval)
            return null;
        const errorLocations = filterNull(retrieval.locations
            .filter((location) => retrieval.documents.some((document) => document.locationId === location.id && document.status === DocumentRetrievalTaskStatus.ERROR))
            .map((location) => location.organizationName));
        if (errorLocations.length === 0)
            return null;
        const uniqueLocations = errorLocations.sort((a, b) => a.localeCompare(b));
        return uniqueLocations;
    }
    get hasErrorLocations() {
        return !!this.errorLocations;
    }
    get recordsSummary() {
        return this.patientConsentUIManager.recordsSummary;
    }
    get aiConfirmButtonText() {
        if (!this.canRedirectExternally) {
            return this.isHealthWalletEnabledForNavigation ? this.openHealthExWalletButtonText : this.goToHealthExButtonText;
        }
        const label = this.returnToPartnerLabel;
        if (label)
            return label;
        return this.isDataUpdate
            ? t("Go to {{ aiToolName }}", { aiToolName: this.aiToolName })
            : t("Finish and return to {{ aiToolName }}", { aiToolName: this.aiToolName });
    }
    get timeToConnectInformation() {
        return this.isAIFlow && this.isRecordsFound ? this.getCustomizedText("timeToConnectInformation") || null : null;
    }
    get errorBannerText() {
        if (this.isAIFlow) {
            return "Due to connection issues, we couldn’t access all records from: <b>{{ providers }}</b>. Records from your other providers were successfully connected.";
        }
        return this.isHealthWalletEnabled
            ? "Due to connection issues, we couldn’t access records from: <b>{{ providers }}</b>. Records from your other connected providers were successfully added to your HealthEx Wallet."
            : "Due to connection issues, we couldn’t access records from: <b>{{ providers }}</b>. Records from your other connected providers were successfully added to your HealthEx Unified Record.";
    }
    get showAiCountdownTimer() {
        if (!this.canRedirectExternally)
            return false;
        return this.isAIFlow && this.isRecordsFound && !this.hasErrorLocations;
    }
    get showRecordsSummary() {
        return this.recordsSummary !== null && (!this.isIAS || !this.isHealthWalletEnabled);
    }
    async onComplete() {
        if (this.isPreview)
            return;
        if (this.isCompleting)
            return;
        this.isCompleting = true;
        this.clearAiCountdownTimer();
        if (this.isOAuthFlow) {
            await this.patientConsentUIManager.completeOAuthAfterPDDE("success");
        }
        else if (this.hasHostedRedirectUrl) {
            await this.patientConsentUIManager.handleRedirectForHostedFlow("success");
        }
        else {
            this.patientConsentUIManager.done();
        }
    }
    openWallet() {
        if (this.isHealthWalletEnabled) {
            this.patientConsentUIManager.closeToPatientHistory();
        }
        else {
            void this.onComplete();
        }
    }
    onRecordsSummaryClick() {
        if (this.isHealthWalletEnabled) {
            this.openWallet();
        }
        else {
            void this.showUnifiedRecord();
        }
    }
    async showUnifiedRecord(type = "pdf") {
        void openAsyncWindow(async () => {
            var _a, _b;
            const url = type === "raw"
                ? (_a = (await this.patientConsentUIManager.getDownloadRawDocumentsLink())) === null || _a === void 0 ? void 0 : _a.downloadLink
                : (_b = (await this.patientConsentUIManager.getDownloadPdfLink())) === null || _b === void 0 ? void 0 : _b.downloadLink;
            if (!url) {
                console.warn(`No URL available`);
                return null;
            }
            return url;
        }).catch((e) => {
            console.error("[PatientConsentConfirmed] Failed to open unified record", e);
        });
    }
}
__decorate([
    observable
], PatientConsentConfirmedViewModel.prototype, "aiCountdownSeconds", void 0);
__decorate([
    observable
], PatientConsentConfirmedViewModel.prototype, "isCompleting", void 0);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "zeroLocationsFound", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "isVectorizationComplete", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "isRecordsFound", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "shouldHideUnifiedRecordButton", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "isAddRecordLocationsDirectly", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "title", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "aiToolName", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "isIASAndStillWaitingForFinalization", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "supportingText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "confirmationText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "confirmationAccessSettingsText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "recordsReadyNotificationText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "shareRecordsAnyTimeText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "addRecordLocationsTimeEstimateText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "walletUpdatesAutomaticallyText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "openYourWalletButtonText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "openHealthExWalletButtonText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "goToHealthExButtonText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "seeUnifiedRecordButtonText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "addRecordLocationsConfirmButtonText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "isEmailContactPreference", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "organizationLogoUrl", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "errorLocations", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "hasErrorLocations", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "recordsSummary", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "aiConfirmButtonText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "timeToConnectInformation", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "errorBannerText", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "showAiCountdownTimer", null);
__decorate([
    computed
], PatientConsentConfirmedViewModel.prototype, "showRecordsSummary", null);
